/* ============================================================
   V5 极简画像页 —— 注册后单页采集 4 字段画像（§7.4）
   ------------------------------------------------------------
   写入 UserProfile：nickname / position / industry / ai_usage_purpose
   开始测评需完成行业/岗位/AI使用目的；跳过返回首页，不启动测评。
   旧 V4 三段式（基础信息→工作问卷→分类结果）整体作废，Phase G/A
   字段已从模型删除。
   ============================================================ */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import client from '../api/client';
import { assessmentReturn } from '../auth/assessmentReturn';
import { missingProfileFields } from '../utils/profile';
import './ProfileSetupPage.css';

// 常用行业下拉 —— 末项「其他」走自由文本兜底
const INDUSTRIES = [
  '互联网 / IT 软件',
  '金融 / 银行 / 保险',
  '教育 / 培训',
  '医疗 / 健康',
  '制造业',
  '零售 / 电商 / 消费',
  '房地产 / 建筑',
  '媒体 / 文化 / 娱乐',
  '政府 / 公共事业 / 非营利',
  '专业服务（咨询 / 法律 / 财会）',
  '能源 / 环保',
  '交通 / 物流',
  '农业',
];
const OTHER = '__other__';

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = assessmentReturn(new URLSearchParams(location.search).get('returnTo'));

  const [nickname, setNickname] = useState('');
  const [position, setPosition] = useState('');
  const [industrySelect, setIndustrySelect] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [aiPurpose, setAiPurpose] = useState('');

  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // 预填已有画像（用户重复进入时）
  useEffect(() => {
    let alive = true;
    setFetching(true);
    setLoadFailed(false);
    setError('');
    client.get('/profile/')
      .then(({ data }) => {
        if (!alive) return;
        setNickname(data.nickname || '');
        setPosition(data.position || '');
        setAiPurpose(data.ai_usage_purpose || '');
        const ind = data.industry || '';
        if (!ind) {
          setIndustrySelect('');
        } else if (INDUSTRIES.includes(ind)) {
          setIndustrySelect(ind);
        } else {
          setIndustrySelect(OTHER);
          setIndustryOther(ind);
        }
      })
      .catch(() => {
        if (alive) {
          setLoadFailed(true);
          setError('画像加载失败，请重试后再填写。');
        }
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [loadAttempt]);

  const resolvedIndustry = industrySelect === OTHER ? industryOther.trim() : industrySelect;

  const handleSave = async () => {
    if (saving || fetching || loadFailed) return;
    const profile = {
      nickname: nickname.trim(), position: position.trim(),
      industry: resolvedIndustry, ai_usage_purpose: aiPurpose.trim(),
    };
    const missing = missingProfileFields(profile);
    if (missing.length) {
      setError('请填写' + missing.join('、') + '，或选择跳过返回首页。');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await client.put('/profile/', profile);
      if (missingProfileFields(data).length) {
        setError('画像尚未完整保存，请检查后重试。');
        return;
      }
      navigate(returnTo || '/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || '保存失败，请稍后再试');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => navigate('/', { replace: true });

  if (fetching) {
    return (
      <div className="v5-profile-setup">
        <div className="vp-loading">加载中…</div>
      </div>
    );
  }

  return (
    <div className="v5-profile-setup">
      <div className="vp-shell">
        <header className="vp-head">
          <span className="vp-logo">AIQ</span>
          <h1>完善你的画像</h1>
          <p className="vp-sub">
            这些信息用于为你定制更贴近实际工作的测评题目，<b>不参与测评打分</b>。
            开始测评前请填写行业、岗位和 AI 使用目的；也可以跳过并返回首页。
          </p>
        </header>

        <div className="vp-card">
          {error && <div className="vp-error" role="alert">{error}</div>}
          {loadFailed && <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>重新加载画像</button>}

          {/* 昵称 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-nickname">
              昵称<span className="vp-tag optional">选填</span>
            </label>
            <input
              id="vp-nickname"
              className="vp-input"
              type="text"
              value={nickname}
              maxLength={20}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="报告里对你的称呼，留空则显示「匿名测评者」"
            />
          </div>

          {/* 岗位 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-position">
              岗位<span className="vp-tag suggest">必填</span>
            </label>
            <input
              id="vp-position"
              className="vp-input"
              type="text"
              value={position}
              maxLength={50}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="如：产品经理、后端工程师、市场专员"
            />
          </div>

          {/* 行业 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-industry">
              行业<span className="vp-tag suggest">必填</span>
            </label>
            <select
              id="vp-industry"
              className="vp-input vp-select"
              value={industrySelect}
              onChange={(e) => setIndustrySelect(e.target.value)}
            >
              <option value="">请选择所在行业</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
              <option value={OTHER}>其他（手动填写）</option>
            </select>
            {industrySelect === OTHER && (
              <input
                className="vp-input vp-input-other"
                type="text"
                value={industryOther}
                maxLength={50}
                onChange={(e) => setIndustryOther(e.target.value)}
                placeholder="请填写你所在的行业"
              />
            )}
          </div>

          {/* AI 使用目的 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-purpose">
              AI 使用目的<span className="vp-tag strong">必填</span>
            </label>
            <textarea
              id="vp-purpose"
              className="vp-input vp-textarea"
              value={aiPurpose}
              maxLength={300}
              rows={3}
              onChange={(e) => setAiPurpose(e.target.value)}
              placeholder="请描述一项具体工作，如：整理客户拜访记录，归纳需求并制定跟进计划；分析销售数据，找出业绩变化的原因。"
            />
            <div className="vp-hint">
              说清楚处理什么、想得到什么结果，比只写“提高效率”更有帮助。
            </div>
          </div>
        </div>

        <div className="vp-actions">
          <button type="button" className="vp-btn ghost" onClick={handleSkip} disabled={saving}>
            跳过
          </button>
          <button type="button" className="vp-btn primary" onClick={handleSave} disabled={saving || loadFailed}>
            {saving ? '保存中…' : '保存并继续 →'}
          </button>
        </div>
      </div>
    </div>
  );
}
